/* eslint-disable react/jsx-max-props-per-line, react/jsx-one-expression-per-line */
import { Link, useParams } from 'react-router-dom';
import { useSetPageTitle } from 'src/hooks/useSetPageTitle';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from 'src/components/ui/table';
import { useListBillingImportLineItemsQuery } from './pipeline.endpoints';
import { BillingPipelineShell } from './billing-pipeline-shell';

export default function PipelineImportLineItemsPage() {
  const { importId } = useParams<{ importId: string }>();
  const idNum = Number(importId);
  const valid = Number.isFinite(idNum) && idNum > 0;

  useSetPageTitle(valid ? `Billing — Import ${idNum} line items` : 'Billing — Import line items');

  const { data, isLoading, isError } = useListBillingImportLineItemsQuery(
    { importId: idNum },
    { skip: !valid }
  );

  const rows = data?.results ?? [];

  const beforeNav = (
    <Link
      to="/billing/pipeline/imports"
      className="text-sm text-blue-600 hover:underline ml-2"
    >
      ← All imports
    </Link>
  );

  if (!valid) {
    return (
      <BillingPipelineShell beforeNav={beforeNav}>
        <p className="text-gray-600">Invalid import id.</p>
      </BillingPipelineShell>
    );
  }

  return (
    <BillingPipelineShell beforeNav={beforeNav}>
      <div className="space-y-2">
        <h1 className="text-lg font-semibold text-gray-900">Staging line items — import #{idNum}</h1>
        <p className="text-sm text-gray-500">
          Read-only list from the pipeline. Lines may have no invoice until grouped.
        </p>

        <div className="rounded-md border border-gray-200 bg-white overflow-x-auto mt-4">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Line ID</TableHead>
                <TableHead>Description</TableHead>
                <TableHead className="text-right">Qty</TableHead>
                <TableHead className="text-right">Rate</TableHead>
                <TableHead className="text-right">Amount</TableHead>
                <TableHead>Validation</TableHead>
                <TableHead>Attention</TableHead>
                <TableHead>Invoice</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {isLoading ? (
                <TableRow>
                  <TableCell colSpan={8} className="text-center py-8 text-gray-500">
                    Loading…
                  </TableCell>
                </TableRow>
              ) : isError ? (
                <TableRow>
                  <TableCell colSpan={8} className="text-center py-8 text-red-600">
                    Failed to load line items.
                  </TableCell>
                </TableRow>
              ) : rows.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={8} className="text-center py-8 text-gray-500">
                    No staging lines for this import.
                  </TableCell>
                </TableRow>
              ) : (
                rows.map((row) => (
                  <TableRow key={row.id}>
                    <TableCell className="font-mono text-xs max-w-[120px] truncate">{row.id}</TableCell>
                    <TableCell className="max-w-[240px] truncate" title={row.description ?? ''}>
                      {row.description ?? '—'}
                    </TableCell>
                    <TableCell className="text-right tabular-nums">{row.qty ?? '—'}</TableCell>
                    <TableCell className="text-right tabular-nums">{row.rate ?? '—'}</TableCell>
                    <TableCell className="text-right tabular-nums">{row.amount_to_bill ?? '—'}</TableCell>
                    <TableCell className="text-xs">{row.validation_status}</TableCell>
                    <TableCell>{row.attention_needed ? 'Yes' : 'No'}</TableCell>
                    <TableCell>
                      {row.invoice ? (
                        <Link
                          className="text-blue-600 text-xs hover:underline"
                          to={`/billing/invoices?invoiceId=${encodeURIComponent(row.invoice)}`}
                        >
                          Open invoice
                        </Link>
                      ) : (
                        '—'
                      )}
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </div>
      </div>
    </BillingPipelineShell>
  );
}
